<template>
  <div>
    <b-card>
      <b-form-row>
        <h5 class="col-12 text-primary font-weight-bold pb-2">
          Harvest Information (non-Clearcut)
        </h5>
      </b-form-row>
      <p v-if="!landownerReport.harvest">
        You indicated that no harvesting was performed in this reporting year. If any acreage
        was harvested, please go back to the General Details page to indicate that the property
        was harvested.
      </p>
      <template v-else>
        <p class="font-sm">
          Total harvest area as reported on the Notification:
          <strong>
            {{harvestDetails && harvestDetails.totalHarvestArea | numberString}} Acres
          </strong>
        </p>
        <p class="font-sm">
          Enter the acres harvested, per category listed below. Please do not report the same
          acres in more than one place. Report only acres that occurred during
          <strong>{{landownerReport.reportingYear}}</strong>.
        </p>
        <ValidationProvider
          name="Partial Cut Harvest"
          rules="max_decimal_singular:1|min_value:0"
          v-slot="{ errors, classes }"
          slim
        >
          <div role="group" class="form-row form-group justify-content-between">
            <b-col class="col-form-label font-inherit" cols="12" lg="6">
              <label for="partialCutHarvest">
                Partial Cut Harvest Acres <FontAwesomeIcon
                focusable="true"
                tabindex="0"
                class="mx-2 text-primary align-self-center label-tip"
                icon="info-circle"
                id="partial-cut-popover"
              />
              </label>
              <b-popover
                target="partial-cut-popover"
                :triggers="['hover', 'focus']"
                placement="right"
              >
                A partial cut harvest removes only a portion of the merchantable trees in a
                stand. This includes commercial thinning, single tree selections, group
                selections, diameter-limit harvests. Record the number of
                <strong>acres harvested</strong> using a partial cut harvest.
              </b-popover>
            </b-col>
            <b-col>
              <b-form-input
                type="number"
                id="partialCutHarvest"
                name="partialCutHarvest"
                v-model="harvestActivities.partialCutHarvest"
                placeholder="Enter Acres"
                :class="classes"
                maxlength="7"
                :aria-describedby="$errorsIdString(errors, 'partialCutHarvest')"
              />
              <InputErrors :errors="errors" errorsIdPrefix="partialCutHarvest" />
            </b-col>
          </div>
        </ValidationProvider>
        <ValidationProvider
          name="Initial/Intermediate Shelterwood Harvest"
          rules="max_decimal_singular:1|min_value:0"
          v-slot="{ errors, classes }"
          slim
        >
          <div role="group" class="form-row form-group justify-content-between">
            <b-col class="col-form-label font-inherit" cols="12" lg="6">
              <label for="initialShelterwoodHarvest">
                Initial/Intermediate Shelterwood Harvest Acres <FontAwesomeIcon
                focusable="true"
                tabindex="0"
                class="mx-2 text-primary align-self-center label-tip"
                icon="info-circle"
                id="initial-shelterwood-popover"
              />
              </label>
              <b-popover
                target="initial-shelterwood-popover"
                :triggers="['hover', 'focus']"
                placement="right"
              >
                A shelterwood harvest system removes most or all the trees from a stand, in two
                or more separate harvests, to regenerate a new stand. The
                <strong>initial harvest</strong> and an optional
                <strong>intermediate harvest</strong> prepares a seed bed and removes most of
                the trees, leaving enough trees to provide seed production and establish new
                seedlings under the shade of the overstory.
              </b-popover>
            </b-col>
            <b-col>
              <b-form-input
                type="number"
                id="initialShelterwoodHarvest"
                name="initialShelterwoodHarvest"
                v-model="harvestActivities.initialShelterwoodHarvest"
                placeholder="Enter Acres"
                :class="classes"
                maxlength="7"
                :aria-describedby="$errorsIdString(errors, 'initialShelterwoodHarvest')"
              />
              <InputErrors :errors="errors" errorsIdPrefix="initialShelterwoodHarvest" />
            </b-col>
          </div>
        </ValidationProvider>
        <ValidationProvider
          name="Final/Overstory Removal Shelterwood Harvest"
          rules="max_decimal_singular:1|min_value:0"
          v-slot="{ errors, classes }"
          slim
        >
          <div role="group" class="form-row form-group justify-content-between">
            <b-col class="col-form-label font-inherit" cols="12" lg="6">
              <label for="finalShelterwoodHarvest">
                Final/Overstory Removal Shelterwood Harvest Acres <FontAwesomeIcon
                focusable="true"
                tabindex="0"
                class="mx-2 text-primary align-self-center label-tip"
                icon="info-circle"
                id="final-shelterwood-popover"
              />
              </label>
              <b-popover
                target="final-shelterwood-popover"
                :triggers="['hover', 'focus']"
                placement="right"
                custom-class="popover-lg"
              >
                <p>
                  A shelterwood harvest system removes most or all the trees from a stand, in
                  two or more separate harvests, to regenerate a new stand. The
                  <strong>final harvest</strong> or <strong>overstory removal</strong> harvests
                  the overstory trees to release established regeneration from competition with
                  the overstory.
                </p>
                <p>
                  Record the number of <strong>acres</strong> harvested using an
                  <strong>initial or intermediate harvest</strong> and the number of acres harvested using
                  an overstory removal.
                </p>
              </b-popover>
            </b-col>
            <b-col>
              <b-form-input
                type="number"
                id="finalShelterwoodHarvest"
                name="finalShelterwoodHarvest"
                v-model="harvestActivities.finalShelterwoodHarvest"
                placeholder="Enter Acres"
                :class="classes"
                maxlength="7"
                :aria-describedby="
                  '#finalShelterwoodHarvestInstructions '
                  + $errorsIdString(errors, 'finalShelterwoodHarvest')
                "
              />
              <InputErrors :errors="errors" errorsIdPrefix="finalShelterwoodHarvest" />
            </b-col>
            <small id="#finalShelterwoodHarvestInstructions" class="mt-3">
              <strong>NOTE:</strong> An <strong>overstory removal</strong> must leave a
              stand of advanced regeneration, stocked with at least 450 trees per acres
              which are least 3 feet in height for softwood trees and 5 feet in height for
              hardwood trees. These standards are outlined in
              <a
                :href="links.chapter20Rules"
                target="_blank"
                rel="noopener noreferrer"
              >
                MFS Rules - Forest Regeneration and Clearcutting Standards</a>. To obtain a
              copy of the rules contact the Maine Forest Service at 207-287-2791 or by
              visiting the
              <a
                :href="links.mfsPublications"
                target="_blank"
                rel="noopener noreferrer"
              >
                Maine Forest Service’s Publication Page</a>.
            </small>
          </div>
        </ValidationProvider>
      </template>
    </b-card>
    <b-card v-if="landownerReport.harvest">
      <b-form-row>
        <h5 class="col-12 text-primary font-weight-bold pb-2">
          Harvest Information (Clearcuts)
        </h5>
      </b-form-row>
      <div class="mb-3">
        <small>
          <strong>NOTE:</strong> There are clear-cut standards developed by the
            Maine Forest Service for each size of clear-cut. See
            <a
              :href="links.chapter20Rules"
              target="_blank"
              rel="noopener noreferrer"
            >
              MFS Rules - Forest Regeneration and Clearcutting Standards</a>.
        </small>
      </div>
      <b-form-row>
          <b-col cols="12" lg="6">
            <span class="font-sm">Clear-Cut Harvest(s)</span>
            <FontAwesomeIcon
              focusable="true"
              tabindex="0"
              class="mx-2 text-primary align-self-center"
              icon="info-circle"
              id="clearcut-popover"
            />
            <b-popover
              target="clearcut-popover"
              :triggers="['hover', 'focus']"
              placement="right"
              custom-class="popover-lg"
            >
              <p>
                A clear-cut harvest is when all or nearly all the standing trees are
                harvested. (A clear-cut occurs, generally, when there is less than 30
                square feet of basal area of trees 4.5" Diameter Breast Height left on
                the harvest site following the harvest.) For a full definition of a
                clear-cut see 12 MRS, chapter 805,sub-chapter 3-A.
              </p>
              <p>
                List the acres of <strong>all the <span style="font-style: italic;">individual</span> clear-cuts</strong>
                created during this harvest.
              </p>
            </b-popover>
          </b-col>
          <b-col>
            <span v-if="!harvestActivities.clearCutHarvests.length">None</span>
            <b-form-row
              v-for="(clearCutHarvest, index) in harvestActivities.clearCutHarvests"
              :key="index"
            >
              <b-col cols="10">
                <ValidationProvider
                  :name="`Clear-Cut Harvest ${index + 1}`"
                  rules="max_decimal_singular:1|min_value:0"
                  v-slot="{ errors, classes }"
                  slim
                >
                  <b-form-group
                    :label-for="`clear-cut-acres-${index + 1}`"
                    label-cols="4"
                    label-class="text-lg-right mr-lg-3"
                  >
                    <template v-slot:label>
                      Acres
                    </template>
                    <b-form-input
                      type="number"
                      :id="`clear-cut-acres-${index + 1}`"
                      :name="`clear-cut-acres-${index + 1}`"
                      v-model="clearCutHarvest.acres"
                      placeholder="Enter Acres"
                      maxlength="7"
                      :class="classes"
                      :aria-describedby="$errorsIdString(errors, `clear-cut-acres-${index + 1}`)"
                    />
                    <InputErrors
                      :errors="errors"
                      :errorsIdPrefix="`clear-cut-acres-${index + 1}`"
                    />
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col class="text-right">
                <b-button
                  variant="danger"
                  @click="removeClearCut(index)"
                  aria-label="Remove clear-cut harvest"
                >
                  <FontAwesomeIcon class="align-self-center" icon="trash-alt" />
                </b-button>
              </b-col>
            </b-form-row>
          </b-col>
        </b-form-row>
        <b-row>
          <b-col cols="12">
            <div class="text-right">
              <a class="form-action-text text-secondary"
                @click="addClearCutHarvest"
              >
                + Add Clear-Cut Harvest Row
              </a>
            </div>
          </b-col>
        </b-row>
    </b-card>
    <b-card>
      <b-form-row>
        <h5 class="col-12 text-primary font-weight-bold pb-2">
          Change of Land Use
        </h5>
      </b-form-row>
      <p v-if="!landownerReport.harvest">
        No harvest was indicated, therefore no change of land use needs to be reported.
      </p>
      <template v-else>
        <p v-if="harvestDetails && harvestDetails.landUseChangeCategoryId" class="font-sm">
          Change of land use as reported on the Notification:
          <strong>
            {{harvestDetails && harvestDetails.landUseChangeArea | numberString}} Acres
          </strong>
          to change to
          <strong>
            {{getLandUseCategory(harvestDetails && harvestDetails.landUseChangeCategoryId)}}
          </strong>
        </p>
        <p class="font-sm">
          If any forested acres were converted to a non-forest land use type <strong>in this
          reporting year</strong>, please indicate the new land use type and acres converted.
          <FontAwesomeIcon
            focusable="true"
            tabindex="0"
            class="mx-2 text-primary align-self-center"
            size="lg"
            icon="info-circle"
            id="land-use-popover"
          />
          <b-popover
            target="land-use-popover"
            :triggers="['hover', 'focus']"
            placement="right"
            custom-class="popover-lg"
          >
            "Change of land use" occurs when the harvest was conducted to convert forest land to
            another land use such as house lots, farm pastures, etc. When there is or will be land
            use change, indicate the new land use type and enter the number of acres on which the
            land use has changed or will change.
          </b-popover>
        </p>
        <b-row>
          <b-col cols="12" lg="6">
            <ValidationProvider
              name="Land Use Category"
              rules="required_if:Land Use Change Area"
              v-slot="{ errors, classes }"
              slim
            >
              <b-form-group
                label-cols="12"
                label-cols-lg="4"
                label-for="landUseCategory"
              >
                <template v-slot:label>
                  New Land Use
                </template>
                <select
                  id="landUseCategory"
                  name="landUseCategory"
                  v-model="harvestActivities.landUseChangeCategoryId"
                  class="form-control"
                  :class="classes"
                  @change="landUseCategoryChange"
                  :aria-describedby="$errorsIdString(errors, 'landUseCategory')"
                >
                  <option :value="null">None</option>
                  <option
                    v-for="option in landUseCategories"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{option.name}}
                  </option>
                </select>
                <InputErrors :errors="errors" errorsIdPrefix="landUseCategory" />
              </b-form-group>
            </ValidationProvider>
          </b-col>
          <b-col cols="12" lg="6">
            <ValidationProvider
              name="Land Use Change Area"
              rules="max_decimal_singular:1|min_value:0.1|required_if:Land Use Category"
              v-slot="{ errors, classes }"
              slim
            >
              <b-form-group label-cols="12" label-cols-lg="6" label-for="landUseChangeArea" label-class="text-lg-right">
                <template v-slot:label>
                  Area of land use change (acres)
                </template>
                <b-form-input
                  type="number"
                  id="landUseChangeArea"
                  name="landUseChangeArea"
                  v-model="harvestActivities.landUseChangeArea"
                  placeholder="Enter Acres"
                  :class="classes"
                  maxlength="7"
                  :aria-describedby="$errorsIdString(errors, 'landUseChangeArea')"
                />
                <InputErrors :errors="errors" errorsIdPrefix="landUseChangeArea" />
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>
      </template>
    </b-card>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import LookupModules from '@/store/moduleConfig';

export default {
  name: 'HarvestActivitiesForm',
  props: {
    landownerReport: {
      type: Object,
      required: true,
    },
    harvestActivities: {
      type: Object,
      required: true,
    },
  },
  created() {
    this.$store.dispatch(`${LookupModules.LAND_USE_CATEGORIES.namespace}/fetchOptions`);
  },
  computed: {
    ...mapState('ActivityDetails', {
      harvestDetails: state => state.activityDetails.harvestDetails,
    }),
    ...mapState('Configuration', {
      links: state => state.links,
    }),
    ...mapState(LookupModules.LAND_USE_CATEGORIES.namespace, {
      landUseCategories: state => state.options,
      landUseCategoriesLoading: state => state.loading,
    }),
    ...mapGetters(LookupModules.LAND_USE_CATEGORIES.namespace, {
      getLandUseCategory: 'getName',
    }),
  },
  methods: {
    addClearCutHarvest() {
      this.harvestActivities.clearCutHarvests.push({ acres: null });
    },
    removeClearCut(index) {
      this.harvestActivities.clearCutHarvests.splice(index, 1);
    },
    landUseCategoryChange(event) {
      if (!event.target.value) this.harvestActivities.landUseChangeArea = null;
    },
  },
};
</script>
